import {useState} from 'react';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import {Link, useNavigate} from 'react-router-dom';
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown
} from 'reactstrap';
import RoleNames from '../../lib/RoleNames';
import {RoutePaths} from '../../RoutePaths';
import {
    LogIn,
    LogOut,
    Rocket,
    ScrollText,
    Settings,
    ShieldCheck,
    UserCircle,
    UserPlus,
    Users
} from 'lucide-react';
import WorkspaceContactsModal from '../utility/modals/WorkspaceContactsModal';

/**
 * The account menu. Company settings and the sysadmin console each get one
 * entry that opens their own shell; the individual pages live in those shells'
 * sidebars rather than here.
 */
const UserMenu = () => {
    const authStateManager = useAuthStateManager();
    const navigate = useNavigate();
    const {isAuthenticated, email} = authStateManager;
    const [contactsOpen, setContactsOpen] = useState(false);

    const isSysAdmin = authStateManager.isAuthorizedForResource({
        authorize: true,
        roles: [RoleNames.SysAdmin]
    });
    const isCustomerUser = authStateManager.isInRole(RoleNames.CustomerUser);
    // Maintainers reach Settings too: it shows them just their directory pages.
    const canOpenSettings = authStateManager.isAuthorizedForResource({
        authorize: true,
        roles: RoleNames.DirectoryEditors
    });

    return (
        <>
            <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret>
                    {isAuthenticated
                        ? email?.padEnd(22, ' ')
                        : 'Menu'.padEnd(18, ' ')}
                </DropdownToggle>
                <DropdownMenu className="dropdown-left">
                    {isAuthenticated ? (
                        <>
                            {canOpenSettings && (
                                <DropdownItem
                                    tag={Link}
                                    to={RoutePaths.settings.root()}
                                >
                                    <Settings size={14} className="me-2" />{' '}
                                    Settings
                                </DropdownItem>
                            )}
                            {isSysAdmin && (
                                <DropdownItem
                                    tag={Link}
                                    to={RoutePaths.sysadmin.root()}
                                >
                                    <ShieldCheck size={14} className="me-2" />{' '}
                                    Admin console
                                </DropdownItem>
                            )}
                            {isCustomerUser && (
                                <DropdownItem
                                    tag={Link}
                                    to={RoutePaths.gettingStarted()}
                                >
                                    <Rocket size={14} className="me-2" />{' '}
                                    Getting started
                                </DropdownItem>
                            )}
                            <DropdownItem
                                tag={Link}
                                to={RoutePaths.accounts.profile()}
                            >
                                <UserCircle size={14} className="me-2" />{' '}
                                Profile
                            </DropdownItem>
                            <DropdownItem onClick={() => setContactsOpen(true)}>
                                <Users size={14} className="me-2" /> Contacts
                            </DropdownItem>
                            <DropdownItem
                                onClick={() => {
                                    authStateManager.logout();
                                    navigate(RoutePaths.home());
                                }}
                            >
                                <LogOut size={14} className="me-2" /> Logout
                            </DropdownItem>
                        </>
                    ) : (
                        <>
                            <DropdownItem
                                tag={Link}
                                to={RoutePaths.accounts.login()}
                            >
                                <LogIn size={14} className="me-2" /> Login
                            </DropdownItem>
                            <DropdownItem
                                tag={Link}
                                to={RoutePaths.accounts.signUp()}
                            >
                                <UserPlus size={14} className="me-2" /> Sign up
                            </DropdownItem>
                        </>
                    )}
                    <DropdownItem divider />
                    <DropdownItem
                        tag={Link}
                        to={RoutePaths.termsAndConditions()}
                    >
                        <ScrollText size={14} className="me-2" /> Terms and
                        Conditions
                    </DropdownItem>
                    <DropdownItem tag={Link} to={RoutePaths.privacy()}>
                        <ShieldCheck size={14} className="me-2" /> Privacy
                    </DropdownItem>
                </DropdownMenu>
            </UncontrolledDropdown>
            <WorkspaceContactsModal
                isOpen={contactsOpen}
                toggle={() => setContactsOpen(open => !open)}
            />
        </>
    );
};

export default UserMenu;
